import { useState } from 'react';
import type { MailerliteChangeStatus, MailerliteRunSummary } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { useMailerliteRun, useMailerliteRuns } from './api.js';

const OUTCOME_TONES = { ok: 'green', partial: 'amber', failed: 'red' } as const;

const STATUS_TONES: Record<MailerliteChangeStatus, 'green' | 'gray' | 'yellow' | 'red'> = {
  pushed: 'green',
  already: 'gray',
  skipped: 'yellow',
  failed: 'red',
};

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  test: 'Test',
  dry_run: 'Dry run',
};

/**
 * Run history with drill-down. The counters on the Schedule card only describe the most
 * recent run, so this is where a "partial" outcome is actually explained.
 */
export function MailerliteRunLog(): JSX.Element {
  const runsQuery = useMailerliteRuns();
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const runs = runsQuery.data ?? [];

  return (
    <Card title="Run log">
      <DataState
        isLoading={runsQuery.isLoading}
        isError={runsQuery.isError}
        error={runsQuery.error}
        isEmpty={runs.length === 0}
        emptyTitle="This integration has not run yet."
        onRetry={() => void runsQuery.refetch()}
      >
        <div className="space-y-2">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              open={openRunId === run.id}
              onToggle={() => setOpenRunId(openRunId === run.id ? null : run.id)}
            />
          ))}
        </div>
      </DataState>
    </Card>
  );
}

function RunRow({
  run,
  open,
  onToggle,
}: {
  run: MailerliteRunSummary;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className="rounded border border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
      >
        <span className="text-slate-400">{open ? '▾' : '▸'}</span>
        <Badge tone={OUTCOME_TONES[run.outcome]}>{run.outcome}</Badge>
        <span className="text-slate-700">{new Date(run.startedAt).toLocaleString()}</span>
        <Badge tone="blue">{TRIGGER_LABELS[run.trigger] ?? run.trigger}</Badge>
        <span className="text-slate-500">
          {run.pushed} sent · {run.failed} failed · {run.skipped} skipped · {run.alreadyPushed}{' '}
          already
          {run.deferred > 0 ? ` · ${run.deferred} deferred` : ''}
        </span>
        {run.startedByName ? (
          <span className="text-slate-400">by {run.startedByName}</span>
        ) : null}
      </button>
      {open ? <RunEntries runId={run.id} message={run.message} /> : null}
    </div>
  );
}

function RunEntries({
  runId,
  message,
}: {
  runId: string;
  message: string | null;
}): JSX.Element {
  const detailQuery = useMailerliteRun(runId);
  const entries = detailQuery.data?.entries ?? [];

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
      {message ? <p className="mb-2 text-sm text-slate-600">{message}</p> : null}
      <DataState
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        error={detailQuery.error}
        isEmpty={entries.length === 0}
        emptyTitle="No contact-level detail was recorded for this run."
        onRetry={() => void detailQuery.refetch()}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="px-2 py-1 font-medium">Contact</th>
                <th className="px-2 py-1 font-medium">Email</th>
                <th className="px-2 py-1 font-medium">Customer</th>
                <th className="px-2 py-1 font-medium">Result</th>
                <th className="px-2 py-1 font-medium">HTTP</th>
                <th className="px-2 py-1 font-medium">Tries</th>
                <th className="px-2 py-1 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="align-top">
                  <td className="px-2 py-1 text-slate-800">{entry.name}</td>
                  <td className="px-2 py-1 text-slate-600">{entry.email ?? '—'}</td>
                  <td className="px-2 py-1 text-slate-600">{entry.customerName ?? '—'}</td>
                  <td className="px-2 py-1">
                    <Badge tone={STATUS_TONES[entry.status]}>{entry.status}</Badge>
                  </td>
                  <td className="px-2 py-1 text-slate-600">{entry.httpStatus ?? '—'}</td>
                  <td className="px-2 py-1 text-slate-600">{entry.attempts}</td>
                  <td className="px-2 py-1 text-slate-600">{entry.detail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DataState>
    </div>
  );
}
